import React, { useState, useEffect, useRef } from "react";
import { AppBar, Tabs, Tab, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import LightLogoutIcon from "../../Assets/Icons/logout_light.svg";

const navItems = [
  { label: "HOME", path: "/Home" },
  { label: "WeddinglyAi Agent", path: "/Home/Ai-Agent" },
  { label: "MY CREATIONS", path: "/Home/My-Weddings" },
  { label: "ADD CREDITS", path: "/Home/Profile?selectedTab=credit" },
  { label: "PROFILE", path: "/Home/Profile?selectedTab=profile" },
  { label: "LOGOUT", path: null },
];

// Fixed width calculation for tabs to prevent layout shifts
const getTabWidth = (label) => {
  const baseWidth = 16; // Base padding
  const charWidth = 9; // Increased width per character
  return baseWidth * 2 + label.length * charWidth; // Both side padding + text width
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: 30,
  boxShadow: "0px 8px 24px 0px rgba(0,0,0,0.16)",
  width: "auto",
  position: "relative",
  minHeight: "50px",
}));

const StyledTabs = styled(Tabs)(() => ({
  position: "relative",
  minHeight: "50px",
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiTabs-flexContainer": {
    height: "50px",
  },
}));

const StyledTab = styled(Tab)(({ theme, width }) => ({
  fontSize: "13px",
  fontWeight: 500,
  color: theme.palette.common.white,
  padding: "12px 16px",
  margin: "0 8px",
  borderRadius: "8px",
  zIndex: 1,
  minWidth: width,
  width: width,
  maxWidth: width,
  minHeight: "46px",
  height: "46px",
  transition: "color 0.2s ease",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  "&.Mui-selected": {
    backgroundColor: "transparent",
    color: "#171717",
    fontWeight: 500,
    boxShadow: "none",
  },
}));

const LogoutTab = styled(Tab)(({ theme, width }) => ({
  boxSizing: "content-box",
  fontSize: "14px",
  fontWeight: 500,
  color: theme.palette.common.white,
  padding: "12px 16px 12px 12px",
  margin: "0 8px",
  borderRadius: "8px",
  zIndex: 1,
  minWidth: width,
  width: width,
  maxWidth: width,
  minHeight: "48px",
  height: "48px",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  "& .MuiTab-wrapper": {
    color: theme.palette.common.white,
  },
}));

const TabIndicator = styled("span")(({ theme, width, left }) => ({
  position: "absolute",
  bottom: 3,
  height: "48px",
  width: width ? `${width}px` : "0",
  left: left ? `${left}px` : "0",
  backgroundColor: theme.palette.common.white,
  borderRadius: 30,
  transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  zIndex: 0,
}));

const TabContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: "2px 0",
  position: "relative",
  height: "50px",
});

const Separator = styled("div")(({ theme }) => ({
  width: "1px",
  height: "32px",
  backgroundColor: theme.palette.common.white,
  position: "absolute",
  left: 4,
  top: "50%",
  transform: "translateY(-50%)",
}));

const TabWrapper = styled("div")({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  cursor: "pointer",
  zIndex: 1,
  "& *": {
    opacity: "1 !important",
  },
});

const LogoutContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
});

const LogoutIcon = styled("img")({
  marginRight: "4px",
  height: "20px",
  width: "20px",
  flexShrink: 0,
});

const TabLabelWrapper = styled("span")({
  whiteSpace: "nowrap",
  display: "block",
});

const StickyNavbar = ({ currentPath = "/", onTabChange }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    left: 0,
  });
  const tabsRef = useRef({});
  const tabContainerRef = useRef(null);

  const getAdjustedTabSizes = () => {
    return navItems.map((item) => {
      // Give extra width to MY WEDDINGS nav item
      if (item.label === "MY CREATIONS") {
        return getTabWidth(item.label) + 10;
      }

      if (item.label === "WeddinglyAi Agent") {
        return getTabWidth(item.label) - 30;
      }

      return getTabWidth(item.label);
    });
  };

  const tabSizesRef = useRef(getAdjustedTabSizes());

  const tabPositionsRef = useRef([]);

  useEffect(() => {
    let currentPosition = 8;
    tabPositionsRef.current = tabSizesRef.current.map((width) => {
      const position = currentPosition;
      currentPosition += width + 16;
      return { width, position };
    });
  }, []);

  useEffect(() => {
    const findActiveTab = () => {
      const nonMatchingPaths = ["/Home/AdminPanel", "/Home/Terms"];
      if (nonMatchingPaths.some((path) => currentPath.includes(path))) {
        setActiveTab(-1);
        return;
      }

      // Special case for AI_Studio paths, it should belongs to My Weddings tab.
      if (currentPath.includes("/Home/AI_Studio/")) {
        const myWeddingsIndex = navItems.findIndex(
          (item) => item.path === "/Home/My-Weddings"
        );
        if (myWeddingsIndex !== -1) {
          setActiveTab(myWeddingsIndex);
          return;
        }
      }

      const [pathWithoutQuery, queryString] = currentPath.split("?");
      const queryParams = new URLSearchParams(queryString || "");
      const selectedTab = queryParams.get("selectedTab");

      // Special case for Profile path with query parameters
      if (pathWithoutQuery === "/Home/Profile" && selectedTab) {
        const tabIndex = navItems.findIndex((item) => {
          if (!item.path) return false;
          return item.path === `${pathWithoutQuery}?selectedTab=${selectedTab}`;
        });

        if (tabIndex !== -1) {
          setActiveTab(tabIndex);
          return;
        }
      }

      let index = navItems.findIndex((item) => {
        if (!item.path) return false;
        return item.path.split("?")[0] === pathWithoutQuery;
      });

      if (index === -1) {
        index = navItems.findIndex((item) => {
          if (!item.path) return false;
          return pathWithoutQuery.includes(item.path.split("?")[0]);
        });
      }

      if (index !== -1) {
        setActiveTab(index);
      } else {
        setActiveTab(-1);
      }
    };

    findActiveTab();
  }, [currentPath]);

  useEffect(() => {
    if (activeTab === -1) {
      setIndicatorStyle({
        width: 0,
        left: 0,
      });
      return;
    }

    if (activeTab >= 0 && tabPositionsRef.current[activeTab]) {
      const { width, position } = tabPositionsRef.current[activeTab];

      if (activeTab === 0) {
        setIndicatorStyle({
          width: width + 8,
          left: position - 4,
        });
      } else {
        setIndicatorStyle({
          width: width,
          left: position,
        });
      }
    }
  }, [activeTab]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    const path = navItems[newValue].path;

    if (onTabChange && path) {
      onTabChange(path);
    }
  };

  const handleLogout = () => {
    window.localStorage.clear();
    window.sessionStorage.clear();
    window.location.assign("/");
  };

  return (
    <StyledAppBar>
      <TabContainer ref={tabContainerRef}>
        <TabIndicator width={indicatorStyle.width} left={indicatorStyle.left} />
        <StyledTabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="main navigation"
        >
          {navItems.map((item, index) => {
            const isLogout = item.label === "LOGOUT";
            const tabWidth = tabSizesRef.current[index];

            if (isLogout) {
              return (
                <TabWrapper key={item.label} onClick={handleLogout}>
                  <Separator />
                  <LogoutTab
                    label={
                      <LogoutContent>
                        <LogoutIcon
                          src={LightLogoutIcon}
                          alt="Logout"
                          width={20}
                          height={20}
                        />
                        {item.label}
                      </LogoutContent>
                    }
                    disableRipple
                    ref={(el) => (tabsRef.current[index] = el)}
                    width={tabWidth}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                    }}
                  />
                </TabWrapper>
              );
            }

            return (
              <StyledTab
                key={item.label}
                label={<TabLabelWrapper>{item.label}</TabLabelWrapper>}
                disableRipple
                ref={(el) => (tabsRef.current[index] = el)}
                width={tabWidth}
              />
            );
          })}
        </StyledTabs>
      </TabContainer>
    </StyledAppBar>
  );
};

export default StickyNavbar;
